import { useMemo, useState } from 'react'
import { Descriptions, Select, Space, Tabs, Tag, Typography } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { CheckOutlined, CloseOutlined, StopOutlined } from '@ant-design/icons'
import { useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { queryKeys } from '@/shared/api/queryKeys'
import { useAuth } from '@/shared/auth/useAuth'
import { usePermissions } from '@/shared/auth/usePermissions'
import { formatDateTime, utcTooltip } from '@/shared/lib/format'
import { formatMoneyValue } from '@/shared/lib/money'
import { PageHeader } from '@/shared/ui/PageHeader'
import { DataTable } from '@/shared/ui/table/DataTable'
import { RowActions } from '@/shared/ui/table/RowActions'
import { CashBranchSelect } from '@/features/cashdesk/CashBranchSelect'
import { CashDecisionModal, type CashDecision } from '@/features/cashdesk/CashDecisionModal'
import { useCashBranches } from '@/features/cashdesk/useCashBranches'
import {
  DISCOUNT_STATES,
  DISCOUNT_STATE_COLOR,
  decideDiscount,
  fetchDiscountPage,
  type Discount,
  type DiscountState,
} from '@/features/cashdesk/salesApi'

type Scope = 'queue' | 'mine'

export function DiscountApprovalsPage() {
  const { t } = useTranslation()
  const { can } = usePermissions()
  const canApprove = can('cash.discount.approve')
  const canApply = can('cash.discount.apply')
  const [scope, setScope] = useState<Scope>(canApprove ? 'queue' : 'mine')

  return (
    <>
      <PageHeader title={t('nav.discountApprovals')} description={t('pageIntro.discountApprovals')} />
      <Tabs
        activeKey={scope}
        onChange={(key) => setScope(key as Scope)}
        items={[
          ...(canApprove
            ? [{ key: 'queue', label: t('discounts.tabs.queue'), children: <DiscountList scope="queue" /> }]
            : []),
          ...(canApply ? [{ key: 'mine', label: t('discounts.tabs.mine'), children: <DiscountList scope="mine" /> }] : []),
        ]}
      />
    </>
  )
}

function DiscountList({ scope }: { scope: Scope }) {
  const { t } = useTranslation()
  const { user } = useAuth()
  const queryClient = useQueryClient()
  const branches = useCashBranches()
  const [state, setState] = useState<DiscountState | null>(scope === 'queue' ? 'PENDING' : null)
  const [branchId, setBranchId] = useState<string | null>(null)
  const [decision, setDecision] = useState<CashDecision | null>(null)

  const filter = useMemo(() => ({ state, branchId }), [state, branchId])
  const fetchPage = useMemo(() => fetchDiscountPage(scope, filter), [scope, filter])

  const columns = useMemo<ColumnsType<Discount>>(() => {
    const details = (row: Discount) => (
      <Descriptions size="small" column={1} bordered>
        <Descriptions.Item label={t('discounts.columns.customer')}>
          {row.subscriber.login} {row.subscriber.name ? `· ${row.subscriber.name}` : ''}
        </Descriptions.Item>
        <Descriptions.Item label={t('discounts.columns.target')}>{targetOf(row)}</Descriptions.Item>
        <Descriptions.Item label={t('discounts.columns.base')}>{formatMoneyValue(row.base)}</Descriptions.Item>
        <Descriptions.Item label={t('discounts.columns.amount')}>
          {formatMoneyValue(row.amount)} ({(row.percentBp / 100).toFixed(2)}%)
        </Descriptions.Item>
        <Descriptions.Item label={t('discounts.columns.reason')}>{row.reason}</Descriptions.Item>
        <Descriptions.Item label={t('discounts.columns.requestedBy')}>{row.requestedBy.name}</Descriptions.Item>
      </Descriptions>
    )
    const targetOf = (row: Discount) =>
      row.invoiceId
        ? t('discounts.targetInvoice', { no: row.invoiceNo ?? t('invoices.draftNumber') })
        : t('discounts.targetPackage', { name: row.planName ?? t('common.dash') })
    const own = (row: Discount) => row.requestedBy.id === user?.id

    return [
      {
        key: 'requestedAt',
        title: t('discounts.columns.requestedAt'),
        width: 170,
        render: (_, row) => <span title={utcTooltip(row.requestedAt)}>{formatDateTime(row.requestedAt)}</span>,
      },
      {
        key: 'state',
        title: t('discounts.columns.state'),
        width: 150,
        render: (_, row) => (
          <Space size={4} wrap>
            <Tag color={DISCOUNT_STATE_COLOR[row.state]}>{t(`discounts.state.${row.state}`)}</Tag>
            {row.decision === 'AUTO' ? <Tag>{t('discounts.decision.AUTO')}</Tag> : null}
          </Space>
        ),
      },
      {
        key: 'customer',
        title: t('discounts.columns.customer'),
        width: 200,
        render: (_, row) => (
          <Space direction="vertical" size={0}>
            <Typography.Text>{row.subscriber.login}</Typography.Text>
            {row.subscriber.name ? <Typography.Text type="secondary">{row.subscriber.name}</Typography.Text> : null}
          </Space>
        ),
      },
      { key: 'target', title: t('discounts.columns.target'), width: 220, render: (_, row) => targetOf(row) },
      {
        key: 'amount',
        title: t('discounts.columns.amount'),
        width: 170,
        align: 'right',
        render: (_, row) => `${formatMoneyValue(row.amount)} (${(row.percentBp / 100).toFixed(2)}%)`,
      },
      {
        key: 'base',
        title: t('discounts.columns.base'),
        width: 140,
        align: 'right',
        render: (_, row) => formatMoneyValue(row.base),
      },
      { key: 'reason', title: t('discounts.columns.reason'), width: 240, render: (_, row) => row.reason },
      { key: 'branch', title: t('discounts.columns.branch'), width: 180, render: (_, row) => row.branch.name },
      {
        key: 'requestedBy',
        title: t('discounts.columns.requestedBy'),
        width: 160,
        render: (_, row) => row.requestedBy.name,
      },
      {
        key: 'decision',
        title: t('discounts.columns.decision'),
        width: 240,
        render: (_, row) =>
          row.decidedBy ? (
            <Space direction="vertical" size={0}>
              <span>
                {row.decidedBy.name} · {formatDateTime(row.decidedAt)}
              </span>
              {row.decisionNote ? <Typography.Text type="secondary">{row.decisionNote}</Typography.Text> : null}
            </Space>
          ) : row.cancelledBy ? (
            <Space direction="vertical" size={0}>
              <span>
                {t('discounts.cancelledBy', { name: row.cancelledBy.name })} · {formatDateTime(row.cancelledAt)}
              </span>
              {row.cancelReason ? <Typography.Text type="secondary">{row.cancelReason}</Typography.Text> : null}
            </Space>
          ) : (
            t('common.dash')
          ),
      },
      {
        key: 'actions',
        title: '',
        width: 56,
        align: 'center',
        fixed: 'right',
        render: (_, row) => (
          <RowActions
            items={[
              {
                key: 'approve',
                label: t('discounts.approve'),
                icon: <CheckOutlined />,
                permission: 'cash.discount.approve',
                hidden: scope !== 'queue' || row.state !== 'PENDING' || own(row),
                onClick: () =>
                  setDecision({
                    title: t('discounts.approveTitle'),
                    okText: t('discounts.approve'),
                    danger: false,
                    warning: t('discounts.approveWarn'),
                    reasonRequired: false,
                    details: details(row),
                    submit: (text) => decideDiscount(row.id, 'approve', text),
                  }),
              },
              {
                key: 'reject',
                label: t('discounts.reject'),
                icon: <CloseOutlined />,
                permission: 'cash.discount.approve',
                danger: true,
                hidden: scope !== 'queue' || row.state !== 'PENDING' || own(row),
                onClick: () =>
                  setDecision({
                    title: t('discounts.rejectTitle'),
                    okText: t('discounts.reject'),
                    danger: true,
                    warning: t('discounts.rejectWarn'),
                    reasonRequired: true,
                    details: details(row),
                    submit: (text) => decideDiscount(row.id, 'reject', text),
                  }),
              },
              {
                key: 'selfHint',
                label: t('cashdesk.selfDecisionHint'),
                permission: 'cash.discount.approve',
                disabled: true,
                hidden: scope !== 'queue' || row.state !== 'PENDING' || !own(row),
                onClick: () => undefined,
              },
              {
                key: 'cancel',
                label: t('discounts.cancel'),
                icon: <StopOutlined />,
                permission: 'cash.discount.apply',
                danger: true,
                hidden: !own(row) || (row.state !== 'PENDING' && row.state !== 'APPROVED'),
                onClick: () =>
                  setDecision({
                    title: t('discounts.cancelTitle'),
                    okText: t('discounts.cancel'),
                    danger: true,
                    warning: t('discounts.cancelWarn'),
                    reasonRequired: true,
                    details: details(row),
                    submit: (text) => decideDiscount(row.id, 'cancel', text),
                  }),
              },
            ]}
          />
        ),
      },
    ]
  }, [t, user?.id, scope])

  return (
    <>
      <Space wrap style={{ marginBottom: 16 }}>
        {scope === 'queue' ? (
          <CashBranchSelect
            branches={branches.all}
            value={branchId}
            onChange={setBranchId}
            loading={branches.isPending}
            allowClear
            placeholder={t('cashdesk.allBranches')}
          />
        ) : null}
        <Select<string>
          style={{ minWidth: 170 }}
          value={state ?? 'ALL'}
          onChange={(value) => setState(value === 'ALL' ? null : (value as DiscountState))}
          options={[
            { value: 'ALL', label: t('cashdesk.allStates') },
            ...DISCOUNT_STATES.map((s) => ({ value: s, label: t(`discounts.state.${s}`) })),
          ]}
        />
      </Space>

      <DataTable<Discount>
        queryKey={[...queryKeys.cashdesk.discounts, scope, filter]}
        fetchPage={fetchPage}
        columns={columns}
        rowKey={(row) => row.id}
        tableId={`discounts-${scope}`}
        urlPrefix={scope === 'queue' ? 'q' : 'm'}
        scrollX={1900}
        stickyFirstColumn
        showTotal={false}
        emptyTitle={t('discounts.empty')}
      />

      <CashDecisionModal
        decision={decision}
        onClose={() => setDecision(null)}
        onDone={() => {
          setDecision(null)
          void queryClient.invalidateQueries({ queryKey: queryKeys.cashdesk.discounts })
          void queryClient.invalidateQueries({ queryKey: queryKeys.invoices.all })
        }}
      />
    </>
  )
}
